import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "./api";
import { useAuth } from "./AuthContext";
import { ArrowLeft, LogOut, Building2, MousePointer, Award } from "lucide-react";

function Bar({ label, count, max }) {
  const pct = max > 0 ? (count / max) * 100 : 0;
  return (
    <div>
      <div className="flex items-baseline justify-between text-sm">
        <span className="text-[#0A192F] truncate">{label}</span>
        <span className="font-mono text-[#16335A] ml-3">{count}</span>
      </div>
      <div className="mt-1 h-1.5 bg-[#16335A]/10 overflow-hidden" style={{ borderRadius: "2px" }}>
        <div className="h-full bg-[#E8C97A]" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

export default function AdminStats() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get("/admin/affiliates/stats").then((r) => setData(r.data)).catch((err) => setError(formatApiError(err)));
  }, []);

  if (error) return <div className="min-h-screen flex items-center justify-center text-[#3A506B] italic bg-[#FDFBF7]">{error}</div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center text-[#3A506B] italic bg-[#FDFBF7]">Cargando...</div>;

  const maxProv = Math.max(1, ...data.per_provider.map((p) => p.count));
  const maxBlock = Math.max(1, ...data.per_block.map((p) => p.count));

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <header className="bg-[#0A192F] text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <button onClick={() => navigate("/admin")} className="inline-flex items-center gap-2 text-sm text-white/80 hover:text-[#E8C97A]"><ArrowLeft size={14} /> Volver</button>
          <div className="flex items-center gap-5">
            <Link to="/admin/affiliates" className="text-sm text-white/80 hover:text-[#E8C97A]">Centro de Afiliados</Link>
            <span className="hidden sm:inline text-sm text-white/70">{user?.email}</span>
            <button onClick={logout} className="text-sm text-white/70 hover:text-[#E8C97A] inline-flex items-center gap-2"><LogOut size={14} /> Salir</button>
          </div>
        </div>
      </header>
      <main className="max-w-6xl mx-auto px-6 lg:px-10 py-10 space-y-8">
        <div>
          <span className="label-eyebrow text-[#16335A]/70">Tracking de afiliación</span>
          <h1 className="mt-2 font-display text-3xl lg:text-5xl text-[#0A192F] tracking-tight">
            <span className="italic text-[#16335A]">Estadísticas</span>
          </h1>
        </div>

        {/* Total card */}
        <div className="bg-[#0A192F] text-white p-8 grid grid-cols-1 md:grid-cols-3 gap-6" style={{ borderRadius: "2px" }}>
          <div>
            <div className="flex items-center gap-2 text-[#E8C97A] text-xs uppercase tracking-[0.12em] font-medium"><MousePointer size={14} /> Total clicks</div>
            <div className="mt-2 font-display text-5xl">{data.total}</div>
          </div>
          <div>
            <div className="flex items-center gap-2 text-[#E8C97A] text-xs uppercase tracking-[0.12em] font-medium"><Building2 size={14} /> Proveedores activos</div>
            <div className="mt-2 font-display text-5xl">{data.per_provider.length}</div>
          </div>
          <div>
            <div className="flex items-center gap-2 text-[#E8C97A] text-xs uppercase tracking-[0.12em] font-medium"><Award size={14} /> Top item</div>
            <div className="mt-2 font-display text-2xl truncate">{data.top_items[0]?._id?.item || "—"}</div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Panel title="Por proveedor">
            {data.per_provider.length === 0 ? <Empty/> :
              <div className="space-y-3">{data.per_provider.map((p, i) => (<Bar key={i} label={p._id || "Otros"} count={p.count} max={maxProv} />))}</div>}
          </Panel>
          <Panel title="Por bloque">
            {data.per_block.length === 0 ? <Empty/> :
              <div className="space-y-3">{data.per_block.map((b, i) => (<Bar key={i} label={b._id || "—"} count={b.count} max={maxBlock} />))}</div>}
          </Panel>
          <Panel title="Por propuesta">
            {data.per_proposal.length === 0 ? <Empty/> :
              <div className="space-y-2 text-sm">
                {data.per_proposal.slice(0, 10).map((p) => (
                  <div key={p._id} className="flex items-center justify-between gap-3 py-1.5 border-b border-[#16335A]/5">
                    <Link to={p.proposal ? `/admin/proposals/${p._id}` : "#"} className="truncate text-[#16335A] hover:underline">
                      {p.proposal?.trip_title || p.proposal?.destination || p.proposal?.client_name || p._id?.slice(0,8)}
                    </Link>
                    <span className="font-mono text-[#16335A]">{p.count}</span>
                  </div>
                ))}
              </div>}
          </Panel>
          <Panel title="Top enlaces">
            {data.top_items.length === 0 ? <Empty/> :
              <div className="space-y-2 text-sm">
                {data.top_items.slice(0, 12).map((it, i) => (
                  <div key={i} className="flex items-center justify-between gap-3 py-1.5 border-b border-[#16335A]/5">
                    <div className="truncate">
                      <span className="text-[#0A192F]">{it._id?.item || "—"}</span>
                      <span className="ml-2 text-xs text-[#3A506B]">· {it._id?.provider || "otros"}</span>
                    </div>
                    <span className="font-mono text-[#16335A]">{it.count}</span>
                  </div>
                ))}
              </div>}
          </Panel>
        </div>
      </main>
    </div>
  );
}

function Panel({ title, children }) {
  return (
    <section className="bg-white border border-[#16335A]/10 p-6" style={{ borderRadius: "2px" }}>
      <h3 className="font-display text-xl text-[#0A192F] mb-4">{title}</h3>
      {children}
    </section>
  );
}
function Empty() {
  return <p className="text-sm text-[#3A506B] italic">Sin datos todavía.</p>;
}
